import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { publicApi } from '@/lib/api';
import { DCA_LINKS } from '@/lib/dcaConfig';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [settings, setSettings] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    publicApi.getSiteSettings().then(res => setSettings(res.data)).catch(() => {});
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Coaching Programs', path: '/coaching-packages' },
    { name: 'Free Resources', path: '/free-resources' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact-us' },
    { name: 'Careersupport.io', path: 'https://careersupport.io', external: true },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Top Bar */}
      <div className="bg-primary text-primary-foreground py-1.5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center text-sm">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4" />
            <span>{settings?.phone || '(912) 376-9006'}</span>
          </div>
          <div className="hidden sm:flex items-center gap-4">
            <span>{settings?.email || 'info@segcareersupport.com'}</span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'glass-header shadow-md' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-1">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <div className="h-24 md:h-28 overflow-hidden flex items-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_career-platform-dev/artifacts/9caxsxh1_seg-career-support-logo.png" 
                  alt="SEG Career Support" 
                  className="h-40 md:h-48 w-auto object-contain scale-100"
                />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6">
              {navLinks.map((link) => (
                link.external ? (
                  <a
                    key={link.path}
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                    className="text-sm font-medium transition-colors hover:text-primary text-accent flex items-center gap-1"
                  >
                    {link.name}
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                    className={`text-sm font-medium transition-colors hover:text-primary ${
                      isActive(link.path) ? 'text-primary' : 'text-foreground'
                    }`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
              <Button onClick={() => window.open(DCA_LINKS.free20, '_blank', 'noopener,noreferrer')} data-testid="schedule-consultation-btn">
                Free 20-Minute Career Strategy Session
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isOpen && (
            <nav className="lg:hidden pb-4 border-t">
              <div className="flex flex-col gap-2 pt-4">
                {navLinks.map((link) => (
                  link.external ? (
                    <a
                      key={link.path}
                      href={link.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setIsOpen(false)}
                      className="px-4 py-2 text-sm font-medium rounded-md text-accent hover:bg-muted flex items-center gap-1"
                    >
                      {link.name}
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  ) : (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setIsOpen(false)}
                      className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                        isActive(link.path)
                          ? 'bg-primary/10 text-primary'
                          : 'text-foreground hover:bg-muted'
                      }`}
                    >
                      {link.name}
                    </Link>
                  )
                ))}
                <div className="px-4 pt-2">
                  <Button className="w-full" onClick={() => { setIsOpen(false); window.open(DCA_LINKS.free20, '_blank', 'noopener,noreferrer'); }}>
                    Free 20-Minute Career Strategy Session
                  </Button>
                </div>
              </div>
            </nav>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;
